import useLandingStore from '../Store/Landing';
import { getStoredAccessToken } from '../Utils/common.util';

export const fetchApi = async (
  route: string,
  payload?: object,
  method = 'POST',
  headers?: object,
  _isRetry = false // Internal flag to prevent infinite retries
): Promise<{
  ok: boolean;
  data: any;
}> => {
  const options = {
    method,
    headers: {
      'Content-Type': 'Application/json',
      accept: '*/*',
      ...headers
    }
  };
  const token = await getToken();
  if (token) {
    options.headers['Authorization'] = token;
  }
  if (payload) {
    options['body'] = JSON.stringify(payload);
  }
  try {
    const res = await fetch(generateUrl(route), options);
    if (res.status === 200) {
      try {
        const response = await res.json();
        return {
          ok: true,
          // eslint-disable-next-line
          data: response.hasOwnProperty('data') ? response.data : response
        };
      } catch (e) {
        return {
          ok: false,
          data: e
        };
      }
    } else if (res.status === 401) {
      // Prevent infinite retry loops
      if (_isRetry) {
        logoutIfAuthenticated();
        return {
          ok: false,
          data: 'Unauthorized'
        };
      }

      const renewed = await renewAccessToken();
      if (renewed) {
        return await fetchApi(route, payload, method, headers, true);
      }
      // Token refresh failed, log out only if a session existed
      logoutIfAuthenticated();
      return {
        ok: false,
        data: 'Unauthorized'
      };
    } else if (res.status === 500) {
      return {
        ok: false,
        data: 'error'
      };
    } else if (res.status === 429) {
      return {
        ok: false,
        data: 'Too Many Requests'
      };
    } else if (res.status === 400) {
      let errorData: unknown = 'Bad Request';
      try {
        errorData = await res.json();
      } catch (e) {
        errorData = e;
      }
      // 400 is a validation/client error, not an expired session
      return {
        ok: false,
        data: errorData
      };
    } else {
      return {
        ok: false,
        data: res
      };
    }
  } catch (e) {
    return {
      ok: false,
      data: e
    };
  }
};

const logoutIfAuthenticated = () => {
  if (getStoredAccessToken()) {
    useLandingStore.getState().setIsThereToken(false);
  }
};

const getToken = async () => {
  const access_token = getStoredAccessToken();
  if (access_token) {
    return 'Bearer ' + access_token;
  }
  return null;
};

const generateUrl = (route: string): string => {
  return process.env.REACT_APP_BASE_URL + route;
};

// Prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

const renewAccessToken = async (): Promise<boolean> => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refresh_token = localStorage.getItem('refresh_token');

      if (refresh_token) {
        // Use native fetch to avoid recursion through fetchApi
        const res = await fetch(generateUrl('/Account/RefreshToken'), {
          method: 'POST',
          headers: {
            'Content-Type': 'Application/json',
            accept: '*/*'
            // Intentionally NO Authorization header for refresh
          },
          body: JSON.stringify({ RefreshToken: refresh_token })
        });

        if (res.status === 200) {
          const response = await res.json();
          const data = response?.data ? response.data : response;

          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);
          return true;
        } else {
          localStorage.setItem('access_token', '');
          localStorage.setItem('refresh_token', '');
          return false;
        }
      } else {
        localStorage.setItem('access_token', '');
        return false;
      }
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export default fetchApi;
